import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, FileText, Receipt, Mail, Trash2, Undo2 } from "lucide-react";

const rupee = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const statusColor = {
  paid: "bg-green-100 text-green-800 border-green-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  unpaid: "bg-neutral-100 text-neutral-700 border-neutral-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
  returned: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const [retOpen, setRetOpen] = useState(false);
  const [retRows, setRetRows] = useState([]);
  const [retReason, setRetReason] = useState("");
  const [retMode, setRetMode] = useState("adjust");
  const [retNote, setRetNote] = useState("");
  const [retBusy, setRetBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load this invoice");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // PDFs need the login token, so fetch them as a file and open in a new tab
  const openPdf = async (path) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Could not open the PDF");
    }
  };

  const sendEmail = async () => {
    try {
      await api.post(`/invoices/${id}/email`);
      toast.success("Invoice emailed to customer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email");
    }
  };

  const addPayment = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/invoices/${id}/payments`, { amount: amt, mode, note });
      toast.success("Payment recorded");
      setAmount(""); setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  // Build one row per (item, rate) with how many pieces can still be returned
  const openReturn = () => {
    const sold = {};
    (inv.items || []).forEach((it) => {
      const key = `${it.item}||${it.rate}`;
      if (!sold[key]) sold[key] = { ...it, qty: 0 };
      sold[key].qty += Number(it.qty || 0);
    });
    (inv.returns || []).forEach((r) => (r.items || []).forEach((it) => {
      const key = `${it.item}||${it.rate}`;
      if (sold[key]) sold[key].qty -= Number(it.qty || 0);
    }));
    const rows = Object.entries(sold)
      .filter(([, v]) => v.qty > 0.001)
      .map(([key, v]) => ({
        key, item: v.item, rate: Number(v.rate || 0), discount_pct: Number(v.discount_pct || 0),
        tax_pct: Number(v.tax_pct || 0), product_id: v.product_id || null,
        max: v.qty, checked: false, qty: String(v.qty),
      }));
    if (rows.length === 0) return toast.error("Everything on this invoice is already returned");
    setRetRows(rows); setRetReason(""); setRetMode("adjust"); setRetNote("");
    setRetOpen(true);
  };
  const updateRow = (key, patch) => setRetRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const rowRefund = (r) => {
    const q = Number(r.qty) || 0;
    const line = q * r.rate;
    const taxable = line - (line * r.discount_pct) / 100;
    return taxable + (taxable * r.tax_pct) / 100;
  };
  const refundTotal = retRows.filter((r) => r.checked).reduce((a, r) => a + rowRefund(r), 0);

  const submitReturn = async () => {
    const chosen = retRows.filter((r) => r.checked);
    if (chosen.length === 0) return toast.error("Tick at least one item to return");
    for (const r of chosen) {
      const q = Number(r.qty);
      if (!q || q <= 0) return toast.error(`Enter a valid quantity for ${r.item}`);
      if (q > r.max + 0.001) return toast.error(`Only ${r.max} of ${r.item} can be returned`);
    }
    setRetBusy(true);
    try {
      await api.post(`/invoices/${id}/returns`, {
        items: chosen.map((r) => ({
          item: r.item, qty: Number(r.qty), rate: r.rate, discount_pct: r.discount_pct,
          tax_pct: r.tax_pct, product_id: r.product_id,
        })),
        reason: retReason, refund_mode: retMode, note: retNote,
      });
      toast.success("Return recorded");
      setRetOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record return");
    } finally {
      setRetBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete invoice ${inv.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      navigate("/invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  if (error) {
    return (
      <div className="space-y-4">
        <Link to="/invoices" className="text-sm text-primary hover:underline flex items-center gap-1"><ArrowLeft size={14} /> Back to invoices</Link>
        <p className="text-red-700">{error}</p>
      </div>
    );
  }
  if (!inv) return <p className="text-muted-foreground">Loading…</p>;

  const total = Number(inv.total || 0);
  const returnTotal = Number(inv.return_total || 0);
  const netTotal = inv.net_total != null ? Number(inv.net_total) : total;
  const paid = Number(inv.paid_amount || 0);
  const balance = Math.max(0, netTotal - paid);

  return (
    <div className="space-y-6" data-testid="invoice-detail">
      <Link to="/invoices" className="text-sm text-primary hover:underline flex items-center gap-1"><ArrowLeft size={14} /> Back to invoices</Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Invoice</p>
          <h1 className="text-3xl font-serif">{inv.number}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => openPdf(`/invoices/${id}/pdf`)}><FileText size={16} className="mr-2" />PDF</Button>
          <Button variant="outline" onClick={() => openPdf(`/invoices/${id}/receipt`)}><Receipt size={16} className="mr-2" />Receipt</Button>
          <Button variant="outline" onClick={openReturn} data-testid="return-items-btn"><Undo2 size={16} className="mr-2" />Return items</Button>
          <Button variant="outline" onClick={sendEmail}><Mail size={16} className="mr-2" />Email</Button>
          <Button variant="outline" onClick={remove}><Trash2 size={16} className="text-red-600" /></Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4 space-y-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Customer</p>
          <p className="font-medium">{inv.customer?.name}</p>
          {inv.customer?.phone ? <p className="text-sm text-muted-foreground">{inv.customer.phone}</p> : null}
          {inv.customer?.email ? <p className="text-sm text-muted-foreground">{inv.customer.email}</p> : null}
          {inv.customer?.address ? <p className="text-sm text-muted-foreground">{inv.customer.address}</p> : null}
        </Card>
        <Card className="p-4 space-y-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Dates</p>
          <p className="text-sm">Date: {inv.date}</p>
          <p className="text-sm">Due: {inv.due_date}</p>
          <Badge variant="outline" className={`mt-1 ${statusColor[inv.status] || ""}`}>{inv.status}</Badge>
        </Card>
        <Card className="p-4 space-y-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Amount</p>
          <p className="text-sm">Total: <span className="font-medium">{rupee(total)}</span></p>
          {returnTotal > 0 && <p className="text-sm">Returned: − {rupee(returnTotal)}</p>}
          {returnTotal > 0 && <p className="text-sm">Net total: <span className="font-medium">{rupee(netTotal)}</span></p>}
          <p className="text-sm">Paid: {rupee(paid)}</p>
          <p className="text-sm">Balance: <span className="font-medium">{rupee(balance)}</span></p>
        </Card>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="py-3 px-4">Item</th>
              <th className="py-3 px-4 text-right">Qty</th>
              <th className="py-3 px-4 text-right">Rate</th>
              <th className="py-3 px-4 text-right">Disc %</th>
              <th className="py-3 px-4 text-right">Tax %</th>
              <th className="py-3 px-4 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(inv.items || []).map((it, i) => (
              <tr key={i} className="border-t">
                <td className="py-3 px-4">{it.item}</td>
                <td className="py-3 px-4 text-right">{it.qty}</td>
                <td className="py-3 px-4 text-right">{rupee(it.rate)}</td>
                <td className="py-3 px-4 text-right">{it.discount_pct || 0}</td>
                <td className="py-3 px-4 text-right">{it.tax_pct || 0}</td>
                <td className="py-3 px-4 text-right font-medium">{rupee(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t p-4 text-sm space-y-1 text-right">
          <p>Subtotal: {rupee(inv.subtotal)}</p>
          {Number(inv.discount_total) > 0 && <p>Discount: − {rupee(inv.discount_total)}</p>}
          {Number(inv.tax_total) > 0 && <p>Tax: {rupee(inv.tax_total)}</p>}
          {Number(inv.shipping) > 0 && <p>Shipping: {rupee(inv.shipping)}</p>}
          {Number(inv.round_off) !== 0 && <p>Round off: {rupee(inv.round_off)}</p>}
          <p className="font-medium text-base">Total: {rupee(total)}</p>
          {returnTotal > 0 && <p>Returns: − {rupee(returnTotal)}</p>}
          {returnTotal > 0 && <p className="font-medium text-base">Net total: {rupee(netTotal)}</p>}
        </div>
      </Card>

      {inv.notes ? (
        <Card className="p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Notes</p>
          <p className="text-sm whitespace-pre-wrap">{inv.notes}</p>
        </Card>
      ) : null}

      {(inv.returns || []).length > 0 && (
        <Card className="p-4 space-y-3" data-testid="returns-list">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Returns</p>
          <ul className="divide-y text-sm">
            {inv.returns.map((r) => (
              <li key={r.id} className="py-2 space-y-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">− {rupee(r.refund_amount)} · <span className="uppercase">{r.refund_mode === "adjust" ? "adjusted" : `refunded ${r.refund_mode}`}</span></span>
                  <span className="text-muted-foreground">{(r.at || "").slice(0, 10)}</span>
                </div>
                <p className="text-muted-foreground">{(r.items || []).map((it) => `${it.item} × ${it.qty}`).join(", ")}</p>
                {(r.reason || r.note) ? <p className="text-muted-foreground">{[r.reason, r.note].filter(Boolean).join(" · ")}</p> : null}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Dialog open={retOpen} onOpenChange={setRetOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Return items · {inv.number}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {retRows.map((r) => (
              <div key={r.key} className="flex items-center gap-3 border rounded-md p-3">
                <input type="checkbox" className="h-4 w-4" checked={r.checked} onChange={(e) => updateRow(r.key, { checked: e.target.checked })} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{r.item}</p>
                  <p className="text-xs text-muted-foreground">{rupee(r.rate)} each · can return up to {r.max}</p>
                </div>
                <Input type="number" className="w-20" min="0" max={r.max} value={r.qty} disabled={!r.checked} onChange={(e) => updateRow(r.key, { qty: e.target.value })} />
                <span className="w-24 text-right text-sm">{r.checked ? rupee(rowRefund(r)) : "—"}</span>
              </div>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>How to settle</Label>
              <Select value={retMode} onValueChange={setRetMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjust">Adjust (reduce balance / store credit)</SelectItem>
                  <SelectItem value="cash">Refund by cash</SelectItem>
                  <SelectItem value="upi">Refund by UPI</SelectItem>
                  <SelectItem value="bank">Refund by bank</SelectItem>
                  <SelectItem value="cheque">Refund by cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={retReason} onChange={(e) => setRetReason(e.target.value)} placeholder="Damaged, wrong item…" />
            </div>
          </div>
          <div>
            <Label>Note</Label>
            <Input value={retNote} onChange={(e) => setRetNote(e.target.value)} />
          </div>
          <p className="text-sm text-right">Refund amount: <span className="font-semibold text-base">{rupee(refundTotal)}</span></p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetOpen(false)}>Cancel</Button>
            <Button onClick={submitReturn} disabled={retBusy || refundTotal <= 0}>Record return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-4 space-y-3">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Payments</p>
        {(inv.payments || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <ul className="divide-y text-sm">
            {inv.payments.map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between gap-3">
                <span>{rupee(p.amount)} · <span className="uppercase">{p.mode}</span>{p.note ? ` · ${p.note}` : ""}</span>
                <span className="flex items-center gap-3 text-muted-foreground">
                  {(p.at || "").slice(0, 10)}
                  <button className="text-primary hover:underline" onClick={() => openPdf(`/invoices/${id}/payments/${p.id}/receipt`)}>Receipt</button>
                </span>
              </li>
            ))}
          </ul>
        )}

        {balance > 0.01 && (
          <div className="grid gap-3 sm:grid-cols-4 items-end pt-2">
            <div>
              <Label>Amount</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(balance)} />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button onClick={addPayment} disabled={busy}>Record payment</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
